type PillarMeta = {
  name: string;
  slug: string;
};

const CANONICAL_PILLARS: Array<PillarMeta & { subPillars: Array<PillarMeta> }> = [
  {
    name: 'Move',
    slug: 'move',
    subPillars: [
      { name: 'Yoga Instructor', slug: 'yoga-instructor' },
      { name: 'Pilates Instructor', slug: 'pilates-instructor' },
      { name: 'Zumba Instructor', slug: 'zumba-instructor' },
      { name: 'Dance Fitness Trainer', slug: 'dance-fitness-trainer' },
      { name: 'Personal Fitness Trainer', slug: 'personal-fitness-trainer' },
      { name: 'Strength Training Coach', slug: 'strength-training-coach' },
      { name: 'Weight Loss Coach', slug: 'weight-loss-coach' },
      { name: 'Functional Fitness Coach', slug: 'functional-fitness-coach' },
      { name: 'Mobility & Flexibility Coach', slug: 'mobility-flexibility-coach' },
      { name: 'Calisthenics Coach', slug: 'calisthenics-coach' },
      { name: 'Martial Arts Coach', slug: 'martial-arts-coach' },
      { name: 'Sports Performance Coach', slug: 'sports-performance-coach' },
      { name: 'Running Coach', slug: 'running-coach' },
    ],
  },
  {
    name: 'Breathe',
    slug: 'breathe',
    subPillars: [
      { name: 'Meditation Instructor', slug: 'meditation-instructor' },
      { name: 'Breathwork Coach', slug: 'breathwork-coach' },
      { name: 'Pranayama Instructor', slug: 'pranayama-instructor' },
      { name: 'Mindfulness Coach', slug: 'mindfulness-coach' },
      { name: 'Stress Management Coach', slug: 'stress-management-coach' },
      { name: 'Sleep Wellness Coach', slug: 'sleep-wellness-coach' },
    ],
  },
  {
    name: 'Heal',
    slug: 'heal',
    subPillars: [
      { name: 'Reiki Trainer', slug: 'reiki-trainer' },
      { name: 'Reiki Practitioner', slug: 'reiki-practitioner' },
      { name: 'Sound Healing Facilitator', slug: 'sound-healing-facilitator' },
      { name: 'Sound Healing Therapist', slug: 'sound-healing-therapist' },
      { name: 'Chakra Healing Instructor', slug: 'chakra-healing-instructor' },
      { name: 'Chakra Healing Consultant', slug: 'chakra-healing-consultant' },
      { name: 'Spiritual Wellness Coach', slug: 'spiritual-wellness-coach' },
      { name: 'Energy Healing Consultant', slug: 'energy-healing-consultant' },
    ],
  },
  {
    name: 'Nourish',
    slug: 'nourish',
    subPillars: [
      { name: 'Dietitian', slug: 'dietitian' },
      { name: 'Nutritionist', slug: 'nutritionist' },
      { name: 'Sports Nutrition Expert', slug: 'sports-nutrition-expert' },
      { name: 'Weight Management Consultant', slug: 'weight-management-consultant' },
      { name: 'Diabetes Wellness Consultant', slug: 'diabetes-wellness-consultant' },
      { name: 'PCOS Wellness Consultant', slug: 'pcos-wellness-consultant' },
    ],
  },
  {
    name: 'Recover',
    slug: 'recover',
    subPillars: [
      { name: 'Ayurveda Consultant', slug: 'ayurveda-consultant' },
      { name: 'Naturopathy Consultant', slug: 'naturopathy-consultant' },
      { name: 'Wellness Consultant', slug: 'wellness-consultant' },
      { name: 'Physiotherapist', slug: 'physiotherapist' },
      { name: 'Rehabilitation Specialist', slug: 'rehabilitation-specialist' },
      { name: 'Posture Correction Expert', slug: 'posture-correction-expert' },
    ],
  },
  {
    name: 'Guide',
    slug: 'guide',
    subPillars: [
      { name: 'Life Coach', slug: 'life-coach' },
      { name: 'Emotional Wellness Coach', slug: 'emotional-wellness-coach' },
      { name: 'Productivity Coach', slug: 'productivity-coach' },
      { name: 'Habit Coach', slug: 'habit-coach' },
      { name: 'Parenting Coach', slug: 'parenting-coach' },
    ],
  },
];

const PILLAR_RULES: Array<PillarMeta & { terms: string[] }> = [
  {
    name: 'Move',
    slug: 'move',
    terms: ['yoga', 'move', 'movement', 'dance', 'fitness', 'trainer', 'sport', 'sports', 'pilates', 'physio', 'physiotherapy', 'rehab', 'rehabilitation', 'strength'],
  },
  {
    name: 'Breathe',
    slug: 'breathe',
    terms: ['breath', 'breathing', 'meditation', 'pranayama', 'mindfulness'],
  },
  {
    name: 'Heal',
    slug: 'heal',
    terms: ['reiki', 'healing', 'energy', 'therapy', 'therapist', 'therapeutic'],
  },
  {
    name: 'Nourish',
    slug: 'nourish',
    terms: ['nutrition', 'diet', 'ayurveda', 'nutritional', 'nourish', 'food'],
  },
  {
    name: 'Recover',
    slug: 'recover',
    terms: ['recovery', 'recover', 'rehab', 'rehabilitation', 'physio', 'injury', 'restoration', 'post-surgical'],
  },
  {
    name: 'Guide',
    slug: 'guide',
    terms: ['guide', 'coaching', 'coach', 'counsel', 'counseling', 'consult', 'consultation', 'mentor', 'advice', 'life'],
  },
];

const LEADING_LABELS = /^(certified|experienced|senior|lead|master|professional|licensed|expert)\s+/i;
const SPLIT_LABELS = /[·,/|]/;

const PILLAR_PRIORITY = ['heal', 'recover', 'nourish', 'breathe', 'guide', 'move'];

const normalizeText = (value: unknown) => String(value ?? '').trim().replace(/\s+/g, ' ');

const humanize = (value: unknown) =>
  normalizeText(value)
    .replace(/[_-]+/g, ' ')
    .replace(/\s*·\s*/g, ' · ')
    .replace(/\s*\/\s*/g, ' / ');

const titleCase = (value: string) =>
  value
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => {
      if (word.length <= 2) return word.toUpperCase();
      return `${word.charAt(0).toUpperCase()}${word.slice(1).toLowerCase()}`;
    })
    .join(' ');

const getFirstNonEmpty = (...values: unknown[]) => {
  for (const value of values) {
    const text = normalizeText(value);
    if (text) return text;
  }
  return '';
};

const getExactCanonicalMatch = (value: unknown): PillarMeta | null => {
  const normalized = normalizeText(value).toLowerCase();
  if (!normalized) return null;

  for (const pillar of CANONICAL_PILLARS) {
    if (normalizeText(pillar.name).toLowerCase() === normalized || normalizeText(pillar.slug).toLowerCase() === normalized) {
      return { name: pillar.name, slug: pillar.slug };
    }

    for (const sub of pillar.subPillars) {
      if (normalizeText(sub.name).toLowerCase() === normalized || normalizeText(sub.slug).toLowerCase() === normalized) {
        return { name: pillar.name, slug: pillar.slug };
      }
    }
  }

  return null;
};

const getPillarScore = (value: string) => {
  const lowered = humanize(value).toLowerCase();
  return PILLAR_RULES.reduce((scoreMap, rule) => {
    const matches = rule.terms.filter((term) => lowered.includes(term)).length;
    scoreMap[rule.slug] = matches;
    return scoreMap;
  }, {} as Record<string, number>);
};

const getBestPillarSlug = (value: string) => {
  const scores = getPillarScore(value);
  let bestSlug = 'guide';
  let bestScore = 0;

  for (const rule of PILLAR_RULES) {
    const score = scores[rule.slug] || 0;
    if (score > bestScore) {
      bestScore = score;
      bestSlug = rule.slug;
      continue;
    }

    if (score === bestScore && score > 0) {
      const currentPriority = PILLAR_PRIORITY.indexOf(bestSlug);
      const candidatePriority = PILLAR_PRIORITY.indexOf(rule.slug);
      if (candidatePriority !== -1 && (currentPriority === -1 || candidatePriority < currentPriority)) {
        bestSlug = rule.slug;
      }
    }
  }

  return bestSlug;
};

export const getDisplayName = (profile: any) => {
  const firstname = normalizeText(profile?.firstname || profile?.firstName);
  const lastname = normalizeText(profile?.lastname || profile?.lastName);

  return getFirstNonEmpty(
    profile?.vendorName,
    [firstname, lastname].filter(Boolean).join(' '),
    profile?.name,
    profile?.fullName,
    profile?.displayName,
    'Instructor'
  );
};

export const getSpecialityLabel = (profileOrLabel: any) => {
  const candidates: string[] = [];

  if (typeof profileOrLabel === 'string') {
    candidates.push(profileOrLabel);
  } else if (profileOrLabel && typeof profileOrLabel === 'object') {
    const fields = [
      profileOrLabel.speciality,
      profileOrLabel.specialty,
      profileOrLabel.classCategory,
      profileOrLabel.upcomingClasses?.[0]?.classCategory,
      profileOrLabel.Skills?.[0],
      profileOrLabel.skills?.[0],
      profileOrLabel.subRole,
      profileOrLabel.category,
      profileOrLabel.role,
    ];

    fields.forEach((field) => {
      if (Array.isArray(field)) {
        field.forEach((item) => {
          const text = normalizeText(item);
          if (text) candidates.push(text);
        });
      } else {
        const text = normalizeText(field);
        if (text) candidates.push(text);
      }
    });
  }

  if (candidates.length === 0) return 'Instructor';

  let bestLabel = '';
  let bestScore = 0;
  let bestPriority = Number.POSITIVE_INFINITY;

  candidates.forEach((candidate) => {
    candidate
      .split(SPLIT_LABELS)
      .map((part) => part.trim())
      .filter(Boolean)
      .forEach((segment) => {
        const cleaned = segment.replace(LEADING_LABELS, '').trim();
        if (!cleaned) return;

        const slug = getBestPillarSlug(cleaned);
        const score = getPillarScore(cleaned)[slug] || 0;
        const priority = PILLAR_PRIORITY.indexOf(slug);

        if (
          score > bestScore ||
          (score === bestScore && priority !== -1 && priority < bestPriority) ||
          (score === bestScore && priority === bestPriority && cleaned.length > bestLabel.length)
        ) {
          bestScore = score;
          bestPriority = priority === -1 ? Number.POSITIVE_INFINITY : priority;
          bestLabel = cleaned;
        }
      });
  });

  return titleCase(bestLabel || candidates[0]);
};

export const getPillarMeta = (value: unknown): PillarMeta => {
  const exact = getExactCanonicalMatch(value);
  if (exact) return exact;

  const text = humanize(value).toLowerCase();
  const bestSlug = getBestPillarSlug(text);
  const match = PILLAR_RULES.find((rule) => rule.slug === bestSlug);

  return match ? { name: match.name, slug: match.slug } : { name: 'Guide', slug: 'guide' };
};
